/**
 * @fileoverview Server-set first-party attribution cookie (Safari ITP extension).
 * @description Edge-safe serialization + merge helpers used by middleware and the
 * client attribution module. A `Set-Cookie` first-party cookie can persist up to
 * ~400 days — longer than Safari's ~7-day localStorage cap.
 *
 * @module lib/analytics/attribution-cookie
 */

import {
  classifyReferrerHost,
  isNonAcquisitionReferrerHost,
  resolveClickIdFromParams,
} from "./click-id-channel"
import {
  ANALYTICS_COOKIE_MAX_AGE_SEC,
  ATTRIBUTION_COOKIE_NAME,
  CONSENT_ANALYTICS_EXPLICIT_COOKIE,
  CONSENT_ANALYTICS_MIRROR_COOKIE,
  REGION_CONSENT_REQUIRED_COOKIE,
} from "./cookie-names"
import { isConsentRequiredRegion } from "./region"

export { ATTRIBUTION_COOKIE_NAME, ANALYTICS_COOKIE_MAX_AGE_SEC as ATTRIBUTION_COOKIE_MAX_AGE_SEC }

/** Compact touch shape stored in the attribution cookie. */
export interface CookieTouch {
  readonly s: string
  readonly m: string
  readonly lp: string
  readonly c?: string
  readonly r?: string
  /** utm_term */
  readonly t?: string
  /** utm_content */
  readonly co?: string
  /** click id value (gclid, fbclid, …) */
  readonly ci?: string
  /** click id param name */
  readonly cit?: string
  readonly at: number
}

/** Compact persisted attribution record in the cookie. */
export interface CookieAttribution {
  readonly fs: number
  readonly tc: number
  readonly f: CookieTouch
  readonly l: CookieTouch
}

const MAX_LEN = 100

/**
 * Truncates a string for cookie / GA4 safety.
 *
 * @param {string} value - Raw string.
 * @param {number} [max=MAX_LEN] - Max length.
 * @returns {string} Clamped string.
 */
export function clampAttributionString(value: string, max = MAX_LEN): string {
  const trimmed = value.trim()
  if (trimmed.length <= max) return trimmed
  const out = trimmed.slice(0, max)
  const last = out.charCodeAt(out.length - 1)
  return last >= 0xd800 && last <= 0xdbff ? out.slice(0, -1) : out
}

function isValidCookieAttribution(parsed: CookieAttribution): boolean {
  return (
    parsed &&
    typeof parsed.fs === "number" &&
    Number.isFinite(parsed.fs) &&
    typeof parsed.tc === "number" &&
    Number.isFinite(parsed.tc) &&
    parsed.f &&
    typeof parsed.f.s === "string" &&
    typeof parsed.f.m === "string" &&
    typeof parsed.f.lp === "string" &&
    parsed.l &&
    typeof parsed.l.s === "string" &&
    typeof parsed.l.m === "string" &&
    typeof parsed.l.lp === "string"
  )
}

/**
 * Hard cap on the cookie value we'll attempt to parse, measured in UTF-16 code
 * units — a cheap, allocation-free upper bound on parse work (browsers also cap
 * a single cookie at ~4KB, so a legitimate payload is far smaller). Bounding the
 * length before any `decodeURIComponent`/`JSON.parse` stops an oversized forged
 * `adsights_attr_v1` from amplifying CPU/latency wherever this runs — notably
 * the edge attribution-write path in `middleware.ts`.
 */
const MAX_ATTRIBUTION_COOKIE_LENGTH = 4096

/**
 * Parses the attribution cookie value (raw JSON or legacy URI-encoded JSON).
 *
 * @param {string | undefined | null} raw - Cookie value.
 * @returns {CookieAttribution | null} Parsed record or null.
 */
export function parseAttributionCookie(raw: string | undefined | null): CookieAttribution | null {
  if (!raw) return null
  if (raw.length > MAX_ATTRIBUTION_COOKIE_LENGTH) return null
  const candidates = [raw]
  try {
    const once = decodeURIComponent(raw)
    if (once !== raw) candidates.push(once)
    const twice = decodeURIComponent(once)
    if (twice !== once) candidates.push(twice)
  } catch {
    // ignore decode failures
  }
  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate) as CookieAttribution
      if (isValidCookieAttribution(parsed)) return parsed
    } catch {
      continue
    }
  }
  return null
}

/**
 * Serializes attribution for cookie storage (raw JSON — callers encode if needed).
 *
 * @param {CookieAttribution} value - Record to encode.
 * @returns {string} JSON payload (not URI-encoded).
 */
export function serializeAttributionCookie(value: CookieAttribution): string {
  return JSON.stringify(value)
}

/**
 * Whether edge middleware may set attribution cookies for this request.
 *
 * @param {string | null | undefined} countryCode - `x-vercel-ip-country`.
 * @param {string | null | undefined} cookieHeader - Raw `Cookie` header.
 * @returns {boolean} True when consent allows attribution persistence.
 */
export function edgeAttributionWriteAllowed(
  countryCode: string | null | undefined,
  cookieHeader: string | null | undefined,
): boolean {
  const header = cookieHeader ?? ""
  if (header.includes(`${CONSENT_ANALYTICS_EXPLICIT_COOKIE}=0`)) return false
  if (header.includes(`${CONSENT_ANALYTICS_MIRROR_COOKIE}=0`)) return false

  const mirrorGranted = header.includes(`${CONSENT_ANALYTICS_MIRROR_COOKIE}=1`)
  const mirrorAbsent = !header.includes(`${CONSENT_ANALYTICS_MIRROR_COOKIE}=`)
  // GPC is a sale/share opt-out (CCPA/CPRA family), not an opt-out of
  // first-party measurement, so `Sec-GPC` does not block first-party
  // attribution persistence at the edge. The EEA/UK/CH consent-for-storage
  // gate (region + explicit opt-in) still applies below.

  const regionRequired =
    header.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=1`) ||
    isConsentRequiredRegion(countryCode)

  if (regionRequired) {
    return (
      mirrorGranted &&
      header.includes(`${CONSENT_ANALYTICS_EXPLICIT_COOKIE}=1`)
    )
  }

  if (mirrorGranted) return true
  return mirrorAbsent
}

/**
 * Reads a cookie value from a raw `Cookie` header.
 *
 * @param {string | null | undefined} cookieHeader - Request cookie header.
 * @param {string} name - Cookie name.
 * @returns {string | undefined} Cookie value if present.
 */
export function readCookieFromHeader(
  cookieHeader: string | null | undefined,
  name: string,
): string | undefined {
  if (!cookieHeader) return undefined
  const parts = cookieHeader.split(";")
  for (const part of parts) {
    const [k, ...rest] = part.trim().split("=")
    if (k === name) return rest.join("=")
  }
  return undefined
}

/**
 * Merges a new acquisition touch into an existing cookie record.
 *
 * @param {CookieAttribution | null} existing - Current cookie state.
 * @param {CookieTouch} touch - New touch.
 * @param {boolean} isAcquisition - Whether this touch counts as acquisition.
 * @returns {CookieAttribution} Merged record.
 */
export function mergeCookieAttribution(
  existing: CookieAttribution | null,
  touch: CookieTouch,
  isAcquisition: boolean,
): CookieAttribution {
  if (!existing) {
    return {
      fs: touch.at,
      tc: isAcquisition ? 1 : 0,
      f: touch,
      l: touch,
    }
  }

  const same =
    existing.l.s === touch.s &&
    existing.l.m === touch.m &&
    (existing.l.c ?? "") === (touch.c ?? "") &&
    (existing.l.t ?? "") === (touch.t ?? "") &&
    (existing.l.co ?? "") === (touch.co ?? "") &&
    (existing.l.ci ?? "") === (touch.ci ?? "")

  if (isAcquisition && !same) {
    return {
      fs: existing.fs,
      tc: existing.tc + 1,
      f: existing.f,
      l: touch,
    }
  }

  return existing
}

/**
 * Builds a touch from edge-visible request signals (UTM, click id, referer).
 * Direct/non-acquisition visits return null so first-touch stays client-side
 * (in-app inference lives there). External referrers without UTM/click ids are
 * included so Safari ITP can persist organic/social/referral via Set-Cookie.
 *
 * @param {string} pathname - Request pathname.
 * @param {string} search - Query string including `?`.
 * @param {string | null | undefined} referer - `Referer` header.
 * @returns {{ touch: CookieTouch, isAcquisition: boolean } | null} Touch or null.
 */
export function touchFromEdgeRequest(
  pathname: string,
  search: string,
  referer: string | null | undefined,
): { touch: CookieTouch; isAcquisition: boolean } | null {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
  const lc = new Map<string, string>()
  for (const [k, v] of params.entries()) {
    const trimmed = v.trim()
    if (trimmed) lc.set(k.toLowerCase(), trimmed)
  }

  const utm = (k: string): string | undefined => lc.get(`utm_${k}`)
  const utmSource = utm("source")
  const utmMedium = utm("medium")
  const campaign = utm("campaign")
  const term = utm("term")
  const content = utm("content")
  const click = resolveClickIdFromParams(lc)

  let refHost: string | undefined
  if (referer) {
    try {
      const host = new URL(referer).hostname.replace(/^www\./, "")
      if (host && !host.endsWith("adsights.ai") && !isNonAcquisitionReferrerHost(host)) {
        refHost = host
      }
    } catch {
      refHost = undefined
    }
  }

  const at = Date.now()
  const landing = clampAttributionString(pathname || "/")

  if (utmSource || utmMedium || campaign || click.clickId) {
    const channel = click.channel
    const refChannel = refHost ? classifyReferrerHost(refHost) : undefined
    const touch: CookieTouch = {
      s: clampAttributionString(
        utmSource ?? channel?.source ?? refChannel?.source ?? refHost ?? "(referral)",
      ),
      m: clampAttributionString(utmMedium ?? channel?.medium ?? refChannel?.medium ?? "(none)"),
      lp: landing,
      at,
      ...(campaign ? { c: clampAttributionString(campaign) } : {}),
      ...(refHost ? { r: clampAttributionString(refHost) } : {}),
      ...(term ? { t: clampAttributionString(term) } : {}),
      ...(content ? { co: clampAttributionString(content) } : {}),
      ...(click.clickId ? { ci: clampAttributionString(click.clickId) } : {}),
      ...(click.clickIdType ? { cit: click.clickIdType } : {}),
    }
    return { touch, isAcquisition: true }
  }

  if (refHost) {
    const channel = classifyReferrerHost(refHost)
    return {
      touch: {
        s: clampAttributionString(channel.source),
        m: clampAttributionString(channel.medium),
        lp: landing,
        at,
        r: clampAttributionString(refHost),
      },
      isAcquisition: true,
    }
  }

  return null
}
